import React, { useState, useEffect } from 'react';
import {
  LockOpen, Info, ArrowClockwise, Trash, X, Check,
} from '@phosphor-icons/react';
import {
  getBranchNames as fetchBranchNames, getGroupCode as fetchGroupCode, getTickets as fetchTickets,
  getChitPledgeCancelList as fetchChitPledgeCancelList, saveData as saveAuditLog,
  deleteChitPledgeAndSecurityLienDetails as deleteChitPledgeAndSecurityLien,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function ChitPledgesSecurityLienRemove() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);
  const [npsGroupCodes, setNpsGroupCodes] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [npsGroupCode, setNpsGroupCode] = useState('');
  const [reason, setReason] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingReason, setPendingReason] = useState('');

  const [branchschema, setBranchschema] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [, setChitgroupId] = useState(null);

  const [touched, setTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  useEffect(() => {
    fetchBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromTicket = () => {
    setNpsGroupCodes([]);
    setNpsGroupCode('');
    setReason('');
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    resetFromTicket();
    setTickets([]);
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setGroup('');
    resetFromGroup();
    setBranchCodeList([]);
    setTickets([]);
  };

  const loadGroups = async (branch) => {
    const schema = branch.branch_code;
    setBranchschema(schema);
    try {
      const res = await fetchGroupCode(schema);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const loadTickets = async (schema, gCode) => {
    try {
      const res = await fetchTickets(schema, gCode);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  const loadNpsGroupCodes = async (schema, gCode, ticketNo) => {
    try {
      const res = await fetchChitPledgeCancelList(schema, gCode, ticketNo);
      setNpsGroupCodes(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('Failed to fetch NPS group codes:', err);
      setNpsGroupCodes([]);
      showToast('error', 'Load Failed', 'Failed to fetch NPS group codes.');
    }
  };

  const handleCaoChange = (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;
    const selected = branchlist.find((x) => x.branch_name === value);
    if (selected) loadGroups(selected);
  };

  const handleGroupChange = (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;
    const selected = branchCodeList.find((x) => x.group_Code === value);
    if (selected) {
      setGroupCode(selected.group_Code);
      setChitgroupId(selected.chitgroup_id);
      loadTickets(branchschema, selected.group_Code);
    }
  };

  const handleTicketChange = (value) => {
    setTicket(value || '');
    resetFromTicket();
    if (!value) return;
    const selected = tickets.find((x) => x.ticketno === value);
    if (selected) loadNpsGroupCodes(branchschema, groupCode, selected.ticketno);
  };

  const handleReasonKeyDown = (e) => {
    const max = 200;
    if (
      reason.length >= max &&
      e.key !== 'Backspace' && e.key !== 'Delete' &&
      e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' &&
      e.key !== 'Tab'
    ) {
      e.preventDefault();
    }
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const npsGroupCodeInvalid = touched && !npsGroupCode;
  const reasonInvalid = touched && (reason.length < 10 || reason.length > 200);

  /** Step 1: validate + open the confirm modal, the same one every other delete screen uses. */
  const confirmDelete = () => {
    setTouched(true);

    if (!npsGroupCode) {
      showToast('warn', 'No Selection', 'Please select an NPS Group Code to delete.');
      return;
    }

    if (reason.length < 10 || reason.length > 200) {
      showToast('warn', 'Reason Required', 'Please enter a valid reason (min 10 characters).');
      return;
    }

    setPendingReason(reason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (isSaving) return;
    setShowConfirmModal(false);
  };

  /** Step 2: fires only after "Yes, Delete" — saves the audit record, then deletes. */
  const doDelete = async () => {
    setIsSaving(true);

    const psTicketNo = Number(ticket);

    // ── Audit log snapshot, saved BEFORE the delete call ──────────────
    const audit = {
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'CHIT_PLEDGE_SECURITY_LIEN_DELETE',
      oldData: {
        branchSchema: branchschema,
        psGroupCode: groupCode,
        psTicketNo,
        npsGroupCode,
      },
      newData: {
        status: 'DELETED',
        npsGroupCode,
      },
      reason,
    };

    try {
      await saveAuditLog([audit]);
    } catch (err) {
      setIsSaving(false);
      setShowConfirmModal(false);
      console.error('Audit save failed:', err);
      showToast('error', 'Audit Save Failed', 'Audit save failed. Delete was not performed.', true);
      return;
    }

    try {
      await deleteChitPledgeAndSecurityLien(branchschema, groupCode, psTicketNo, npsGroupCode);
      handleDeleteSuccess(npsGroupCode);
    } catch (err) {
      // API may return a plain-text "Success" body that axios fails to
      // parse as JSON, landing here even on a 200 OK — same quirk as
      // the Angular HttpClient version.
      const isSuccess =
        err?.response?.status === 200 ||
        (typeof err?.response?.data === 'string' && err.response.data.toLowerCase() === 'success');

      if (isSuccess) {
        handleDeleteSuccess(npsGroupCode);
      } else {
        setIsSaving(false);
        setShowConfirmModal(false);
        console.error('Delete failed:', err);
        showToast('error', 'Delete Failed', 'Audit saved, but delete failed. Please try again.', true);
      }
    }
  };

  const handleDeleteSuccess = (deletedNpsGroupCode) => {
    setNpsGroupCodes((prev) => prev.filter((x) => x.npS_GROUPCODE !== deletedNpsGroupCode));
    setIsSaving(false);
    setShowConfirmModal(false);
    showToast('success', 'Success', 'Audit saved and chit pledge / security lien deleted successfully.', true);
    handleClear();
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="chit-lien-remove-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="chit-lien-remove-form">
        {isSaving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LockOpen size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Chit Management</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Chits Pledges, Security Lien Remove</div>
          </div>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to load NPS group codes</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="chit-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!cao}
              error={groupInvalid}
              testId="chit-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket"
              emptyMessage="Select a group first"
              disabled={!group}
              error={ticketInvalid}
              testId="chit-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!ticket} hint="Select a ticket in section A first">
        {/* SECTION B: NPS Group Code */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select NPS Group Code</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose the pledge/security lien record to remove</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              NPS Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`nps-${formKey}`}
              options={npsGroupCodes}
              getLabel={(o) => o.npS_GROUPCODE}
              getValue={(o) => o.npS_GROUPCODE}
              value={npsGroupCode}
              onChange={(v) => setNpsGroupCode(v || '')}
              placeholder="Select NPS group code"
              emptyMessage="Select a ticket first"
              disabled={!ticket}
              error={npsGroupCodeInvalid}
              testId="chit-nps-group-select"
            />
            {npsGroupCodeInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>NPS Group Code is required</div>}
          </div>
        </div>
        </SectionLock>

        {/* SECTION C: Reason */}
        {npsGroupCode && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a reason for this deletion (min 10 characters)</div>
              </div>
            </div>

            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Reason <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <textarea
              className="input-field"
              style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
              rows={3}
              placeholder="Enter reason (min 10 characters)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={handleReasonKeyDown}
              data-testid="chit-reason"
            />
            {reasonInvalid && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                {reason.length === 0 ? 'Reason is required.' : reason.length < 10 ? 'Minimum 10 characters required.' : 'Maximum 200 characters allowed.'}
              </div>
            )}
          </>
        )}

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={isSaving} data-testid="chit-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              className="btn-primary"
              style={{ background: '#DC2626', borderColor: '#DC2626' }}
              disabled={isSaving || !npsGroupCode}
              data-testid="chit-delete-button"
            >
              <Trash size={14} style={{ marginRight: '4px' }} /> {isSaving ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="chit-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Trash size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Chit Pledge / Security Lien Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to delete the chit pledge and security lien for NPS Group Code <strong>{npsGroupCode}</strong>. This action cannot be undone.
              </p>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={isSaving} className="btn-secondary" data-testid="chit-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={isSaving}
                data-testid="chit-confirm-delete"
                style={{
                  display: 'inline-flex', alignItems: 'center', padding: '8px 16px', fontSize: '13px', fontWeight: '600',
                  color: 'white', background: isSaving ? '#F3A6A6' : '#DC2626', border: 'none', borderRadius: '6px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                }}
              >
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChitPledgesSecurityLienRemove;